import { Link } from "react-router-dom";
import {
  Truck,
  ShieldCheck,
  Package,
  Sparkles,
  Calendar,
  Layers,
  Boxes,
  Tag,
  Recycle,
  CircleCheck,
  GlassWater,
  MapPin,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison spéciale Rennemoulin ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison spéciale village
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Livraison de cartons de déménagement spéciale {city.aCity}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            La configuration typique de notre village et ses voies
            d&apos;accès préservées exigent une logistique agile et
            sur-mesure que les grands transporteurs nationaux ne peuvent
            pas toujours assurer.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Véhicules adaptés au village",
              body: "Pas de gros camions bloqués au milieu des ruelles. Nos livreurs utilisent des utilitaires légers, parfaitement adaptés au gabarit des rues de Rennemoulin et des communes voisines (Noisy-le-Roi, Bailly, Fontenay-le-Fleury, Villepreux).",
              Icon: Truck,
            },
            {
              title: "Service « Dépose Sérénité »",
              body: "Nous vous livrons vos cartons directement sur le pas de votre porte, ou à l'endroit exact de votre propriété qui vous arrange le plus pour stocker vos fournitures à plat.",
              Icon: MapPin,
            },
            {
              title: "Créneau respecté à la minute",
              body: "Vous fixez l'heure de livraison selon votre emploi du temps. Nous nous engageons sur une ponctualité stricte pour vous éviter toute attente inutile.",
              Icon: Calendar,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Protection Ultra-Fragile ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Protection ultra-fragile
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Sécurisez la vaisselle et le patrimoine familial
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Les demeures de la plaine de Versailles regorgent souvent de
            vaisselle de famille, de verrerie fine ou d&apos;objets de
            décoration précieux. Pour éviter le moindre bris, notre gamme
            intègre des solutions professionnelles spécifiques.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {[
            {
              title: "Les cartons à croisillons (verres et assiettes)",
              body: "C'est le secret des déménageurs professionnels. Ces cartons intègrent des alvéoles en carton lourd qui isolent chaque verre et chaque assiette individuellement. Plus aucun objet ne s'entrechoque pendant les virages ou les freinages.",
              Icon: GlassWater,
            },
            {
              title: "Le film à bulles haute densité",
              body: "Épais et doté de bulles ultra-résistantes, il absorbe l'intégralité des ondes de choc. Enveloppez vos bibelots et pièces de valeur un par un avant de les sceller avec notre adhésif à forte adhérence pour une sécurité maximale.",
              Icon: Layers,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/emballage-protection"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-rennemoulin-cta-protection"
          >
            <ShieldCheck className="h-4 w-4" strokeWidth={2.6} />
            Voir nos protections fragile
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/cartons-demenagement?categorie=croisillons"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-rennemoulin-cta-croisillons"
          >
            <GlassWater className="h-4 w-4" strokeWidth={2.6} />
            Cartons à croisillons
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Offre Malin-Écolo -40% ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Offre malin-écolo
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Nos cartons de second choix à&nbsp;-40%
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Parce que les habitants de {city.name} sont
              particulièrement sensibles à la préservation de leur
              environnement et à la réduction des déchets, découvrez
              notre filière de reconditionnement exclusive.
            </p>

            <div className="mt-6 space-y-4">
              {[
                {
                  title: "Utilisés une seule fois",
                  body: "Issus de déménagements de standing ou de transferts de bureaux, ces cartons pros n'ont servi qu'une seule fois et sont dans un état de solidité proche du neuf.",
                  Icon: Recycle,
                },
                {
                  title: "Contrôlés et validés",
                  body: "Chaque boîte est inspectée manuellement par nos équipes pour s'assurer que sa double cannelure et sa résistance à l'écrasement sont intactes.",
                  Icon: ShieldCheck,
                },
                {
                  title: "-40% d'économie immédiate",
                  body: "Vous profitez du meilleur matériel de déménagement du marché à un prix d'occasion défiant toute concurrence. Une solution économique, intelligente et éco-responsable !",
                  Icon: Tag,
                },
              ].map(({ title, body, Icon }) => (
                <div
                  key={title}
                  className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
                >
                  <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <div>
                    <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                      {title}
                    </h3>
                    <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                      {body}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <Link
              to="/cartons-reconditionnes"
              className="mt-7 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="city-rennemoulin-cta-reconditionnes"
            >
              <Recycle className="h-4 w-4" strokeWidth={2.6} />
              Voir les cartons reconditionnés
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>

          <div className="lg:col-span-5 bg-[#EEF3F7] border border-[#cfdbe6] rounded-3xl p-6">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-full bg-[#237cb5] text-white">
                <Tag className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <div className="font-display font-extrabold text-[20px] text-[#0f1d2b] leading-tight">
                Le pack malin-écolo
              </div>
            </div>
            <p className="mt-4 text-[14px] text-[#3a4856] leading-relaxed">
              La qualité d&apos;un carton pro neuf, à un prix
              d&apos;occasion. Triés, contrôlés, prêts à voyager une
              seconde fois.
            </p>
            <div className="mt-5 space-y-2.5 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Un seul voyage au compteur
              </div>
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Sélection à la main
              </div>
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Double cannelure intacte
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Économie -40% immédiate
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-rennemoulin-cta-shop"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir toute la gamme de cartons
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-rennemoulin-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
